"use client";

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Package, AlertTriangle, CheckCircle, Activity, Clock, ChevronRight, Tablet, Box } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { apiFetchJson } from '../../lib/api';

type DashboardStats = {
  total_stock: number;
  total_items: number;
  critical_items: number;
  warning_items: number;
  healthy_items: number;
  recent_activities: Array<{
    id: number;
    timestamp: string;
    actor_info: string;
    details: string;
    device_id?: string;
    transaction_id?: number;
    consumable_id?: number;
    consumable_name?: string;
  }>;
};

export default function Dashboard() {
  const router = useRouter();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchStats() {
      try {
        const data = await apiFetchJson<DashboardStats>('/dashboard/stats');
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Fehler beim Laden');
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 opacity-30">
        <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin mb-4" />
        <p className="text-sm font-bold uppercase tracking-widest">Dashboard wird geladen...</p>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="p-8 bg-red-50 border border-red-100 rounded-2xl text-red-600 flex flex-col items-center gap-4">
        <AlertTriangle size={48} />
        <div className="text-center">
          <p className="font-bold text-lg">Huston, wir haben ein Problem</p>
          <p className="text-sm opacity-80">{error || 'Daten konnten nicht abgerufen werden'}</p>
        </div>
        <button onClick={() => window.location.reload()} className="btn-primary mt-2">Erneut versuchen</button>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Artikelanzahl',
      value: stats.total_items.toLocaleString(),
      sub: `Gesamtbestand: ${stats.total_stock}`,
      icon: Package,
      color: 'text-primary',
      bg: 'bg-primary-soft',
      action: () => router.push('/consumables')
    },
    {
      label: 'Kritisch (Rot)',
      value: stats.critical_items,
      sub: 'Unter Mindestmenge',
      icon: AlertTriangle,
      color: 'text-red-500',
      bg: 'bg-red-50',
      action: () => router.push('/consumables?traffic_light=red')
    },
    {
      label: 'Warnungen (Gelb)',
      value: stats.warning_items,
      sub: 'Im Warnbereich',
      icon: AlertTriangle,
      color: 'text-amber-500',
      bg: 'bg-amber-50',
      action: () => router.push('/consumables?traffic_light=yellow')
    },
    {
      label: 'Verfügbarkeit',
      value: stats.total_items > 0 ? `${Math.round((stats.healthy_items / stats.total_items) * 100)}%` : '100%',
      sub: 'Im grünen Bereich',
      icon: CheckCircle,
      color: 'text-emerald-500',
      bg: 'bg-emerald-50',
      action: () => router.push('/consumables?traffic_light=green')
    },
  ];

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-700">
      <div className="mb-10 flex items-center gap-5">
        <img src="/logo_schild_green.png" alt="Meditrack Logo" className="h-14 w-auto object-contain" />
        <div>
          <h1 className="text-3xl font-bold text-sedenius-navy leading-tight">Meditrack NG</h1>
          <p className="text-text-secondary mt-1 text-sm font-medium">Praxis Management Plattform</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
        {statCards.map((stat, i) => (
          <div
            key={i}
            onClick={stat.action}
            className="group bg-white rounded-2xl shadow-sm border border-border p-8 flex items-center gap-6 cursor-pointer hover:shadow-xl hover:translate-y-[-4px] transition-all duration-300"
          >
            <div className={`p-4 rounded-2xl ${stat.bg} ${stat.color} transition-transform group-hover:scale-110 duration-300`}>
              <stat.icon size={28} />
            </div>
            <div>
              <p className="text-xs font-bold text-text-secondary uppercase tracking-widest opacity-80">{stat.label}</p>
              <h3 className="text-3xl font-black text-text-primary my-1 tracking-tight">{stat.value}</h3>
              <p className="text-[13px] font-medium text-text-secondary">{stat.sub}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-border overflow-hidden">
        <div className="p-6 border-b border-border/50 flex items-center justify-between">
          <h2 className="text-xl font-bold text-text-primary flex items-center gap-3">
            <Activity size={22} className="text-primary" />
            Letzte Aktivitäten
          </h2>
          <button onClick={() => router.push('/audit')} className="text-xs font-bold text-primary hover:underline flex items-center gap-1 uppercase tracking-wider">
            Alle sehen <ChevronRight size={14} />
          </button>
        </div>

        {stats.recent_activities && stats.recent_activities.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#fcfdfe] text-[11px] font-bold text-text-secondary uppercase tracking-widest border-b border-border/50">
                <tr>
                  <th className="p-5">Zeitstempel</th>
                  <th className="p-5">Akteur</th>
                  <th className="p-5">Asset</th>
                  <th className="p-5">Aktion</th>
                  <th className="p-5">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {stats.recent_activities.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-5 text-[14px] text-text-secondary whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="opacity-40" />
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </td>
                    <td className="p-5 font-bold text-text-primary text-[15px]">
                      <div className="flex flex-col">
                        {log.actor_info}
                        {log.device_id && (
                          <div className="text-[10px] text-primary flex items-center gap-1 mt-1 font-bold">
                            <Tablet size={10} /> {log.device_id}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-5">
                      {log.consumable_id ? (
                        <div className="group relative inline-block">
                          <Link href={`/consumables/${log.consumable_id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors border border-blue-100 font-medium text-xs">
                            <Box size={14} className="opacity-70" />
                            ID: {log.consumable_id}
                          </Link>
                          {log.consumable_name && (
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2.5 py-1.5 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              {log.consumable_name}
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="p-5">
                      <span className={`px-2.5 py-1 rounded-md font-mono text-[11px] font-bold ${log.transaction_id ? "bg-primary-soft text-primary border border-primary/10" : "bg-gray-100 text-gray-600"
                        }`}>
                        {log.transaction_id ? `TX #${log.transaction_id}` : `SYS #${log.id}`}
                      </span>
                    </td>
                    <td className="p-5 text-sm text-text-secondary font-medium leading-relaxed max-w-md">{log.details || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-20 text-center text-text-secondary opacity-40">
            <Clock size={48} className="mx-auto mb-4" />
            <p className="font-bold uppercase tracking-widest text-xs">Noch keine Aktivitäten erfasst</p>
          </div>
        )}
      </div>
    </div>
  );
}
